import { useCallback, useLayoutEffect, useRef } from "react";

/** Follow the transcript or retain the last reading position across layout changes. */
export function useChatScroll(hasTranscript: boolean) {
	const scroller = useRef<HTMLDivElement>(null);
	const following = useRef(true);
	const readingTop = useRef(0);
	const frame = useRef<number | null>(null);

	const settleScroll = useCallback(() => {
		if (frame.current !== null) return;
		frame.current = requestAnimationFrame(() => {
			const el = scroller.current;
			if (el && el.clientHeight > 0) {
				el.scrollTop = following.current ? el.scrollHeight : readingTop.current;
			}
			frame.current = null;
		});
	}, []);

	// Container queries can move WebKit's scroll position during a commit's layout passes.
	useLayoutEffect(() => { settleScroll(); });

	useLayoutEffect(() => {
		const el = scroller.current;
		if (!el) return;
		const cancel = () => {
			if (frame.current !== null) cancelAnimationFrame(frame.current);
			frame.current = null;
		};
		// Direct scrolling takes precedence over a pending layout correction.
		const keys = (event: KeyboardEvent) => {
			if (["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " "].includes(event.key)) cancel();
		};
		el.addEventListener("wheel", cancel, { passive: true });
		el.addEventListener("touchmove", cancel, { passive: true });
		el.addEventListener("pointerdown", cancel, { passive: true });
		el.addEventListener("keydown", keys);
		const viewport = window.visualViewport;
		viewport?.addEventListener("resize", settleScroll);
		const observer = typeof ResizeObserver === "function" ? new ResizeObserver(settleScroll) : null;
		observer?.observe(el);
		if (el.firstElementChild) observer?.observe(el.firstElementChild);
		return () => {
			cancel();
			observer?.disconnect();
			viewport?.removeEventListener("resize", settleScroll);
			el.removeEventListener("wheel", cancel);
			el.removeEventListener("touchmove", cancel);
			el.removeEventListener("pointerdown", cancel);
			el.removeEventListener("keydown", keys);
		};
	}, [hasTranscript, settleScroll]);

	const onScroll = useCallback(() => {
		const el = scroller.current;
		if (!el || frame.current !== null || el.clientHeight === 0) return;
		readingTop.current = el.scrollTop;
		following.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
	}, []);

	const followLatest = useCallback(() => {
		following.current = true;
		settleScroll();
	}, [settleScroll]);

	return { scroller, onScroll, followLatest };
}
